import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { AppLayout } from "@/components/AppLayout";
import {
  BarChart3, TrendingUp, MessageSquare, Heart, Brain, Target, Flame,
  Loader2, Calendar, Zap, Users, BookOpen, Briefcase, Volume2,
  ArrowUpRight, ArrowDownRight, Minus
} from "lucide-react";
import { cn } from "@/lib/utils";

const MODULE_META = {
  friend: { label: "The Friend", icon: Heart, color: "text-pink-500", bg: "bg-pink-500" },
  psychologist: { label: "The Psychologist", icon: Brain, color: "text-violet-500", bg: "bg-violet-500" },
  mentor: { label: "The Mentor", icon: TrendingUp, color: "text-amber-500", bg: "bg-amber-500" },
  social_guide: { label: "Social Guide", icon: Users, color: "text-cyan-500", bg: "bg-cyan-500" },
  job_guardian: { label: "Job Guardian", icon: Briefcase, color: "text-emerald-500", bg: "bg-emerald-500" },
  life_coach: { label: "Life Coach", icon: Target, color: "text-orange-500", bg: "bg-orange-500" },
  storyteller: { label: "Storyteller", icon: BookOpen, color: "text-blue-500", bg: "bg-blue-500" },
  wisdom_roundtable: { label: "Wisdom Council", icon: Volume2, color: "text-red-500", bg: "bg-red-500" },
};

function StatCard({ icon: Icon, label, value, sub, color = "text-primary", testId }) {
  return (
    <div data-testid={testId} className="bg-card border border-border/50 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center bg-primary/10")}>
          <Icon className={cn("w-4 h-4", color)} strokeWidth={1.5} />
        </div>
        <p className="text-[11px] text-muted-foreground">{label}</p>
      </div>
      <p className="text-2xl font-bold tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function MiniBar({ height, maxHeight = 40, active }) {
  return (
    <div className="flex-1 flex items-end justify-center">
      <div
        className={cn("w-full max-w-[8px] rounded-t-sm transition-all", active ? "bg-primary" : "bg-primary/20")}
        style={{ height: `${Math.max(height * (maxHeight / 100), 2)}px` }}
      />
    </div>
  );
}

export default function InsightsPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/insights`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [token]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (!data) {
    return (
      <AppLayout>
        <div className="text-center py-20">
          <BarChart3 className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No insights available yet. Start chatting to see your patterns!</p>
        </div>
      </AppLayout>
    );
  }

  const o = data.overview;
  const ws = data.weekly_summary;
  const maxDailyMsgs = Math.max(...data.chat_activity.map(d => d.messages), 1);
  const maxModuleUsage = data.module_usage.length > 0 ? data.module_usage[0].messages : 1;
  const totalMoodDist = (data.mood_distribution.positive + data.mood_distribution.neutral + data.mood_distribution.negative) || 1;

  return (
    <AppLayout>
      <div className="space-y-6 pb-8" data-testid="insights-page">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" /> Your Insights
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">How you use Omni — patterns, trends, and highlights</p>
        </div>

        {/* Weekly Summary Banner */}
        <div data-testid="weekly-summary" className="bg-primary/5 border border-primary/20 rounded-2xl p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">This Week</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xl font-bold">{ws.conversations}</p>
              <p className="text-[11px] text-muted-foreground">Conversations</p>
            </div>
            <div>
              <p className="text-xl font-bold">{ws.messages}</p>
              <p className="text-[11px] text-muted-foreground">Messages</p>
            </div>
            <div>
              <p className="text-xl font-bold">{ws.mood_entries}</p>
              <p className="text-[11px] text-muted-foreground">Mood Logs</p>
            </div>
            <div>
              <p className="text-xl font-bold">{ws.avg_mood ?? "—"}</p>
              <p className="text-[11px] text-muted-foreground">Avg Mood</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={MessageSquare} label="Total Conversations" value={o.total_conversations} testId="stat-conversations" />
          <StatCard icon={Zap} label="Total Messages" value={o.total_messages.toLocaleString()} testId="stat-messages" />
          <StatCard icon={Heart} label="Mood Entries" value={o.total_mood_entries} sub={o.mood_streak > 0 ? `${o.mood_streak}-day streak` : null} testId="stat-mood-entries" />
          <StatCard icon={Target} label="Goals" value={`${o.goals_completed}/${o.goals_active + o.goals_completed}`} sub="completed" testId="stat-goals" color="text-emerald-500" />
        </div>

        {/* Chat Activity Chart */}
        <div data-testid="chat-activity-chart" className="bg-card border border-border/50 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold">Chat Activity</p>
              <p className="text-[11px] text-muted-foreground">Messages per day — last 30 days</p>
            </div>
            <p className="text-xs text-muted-foreground">{o.avg_messages_per_session} avg/session</p>
          </div>
          <div className="flex items-end gap-[2px] h-12">
            {data.chat_activity.map((d, i) => {
              const pct = maxDailyMsgs > 0 ? (d.messages / maxDailyMsgs) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex items-end justify-center group relative">
                  <div
                    className={cn("w-full max-w-[7px] rounded-t-sm transition-all", d.messages > 0 ? "bg-primary" : "bg-muted/30")}
                    style={{ height: `${Math.max(pct * 0.48, 2)}px` }}
                  />
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-popover border border-border rounded-lg px-2 py-1 text-[10px] whitespace-nowrap shadow-lg z-10">
                    {d.date}: {d.messages} msgs
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-muted-foreground/50">
            <span>30d ago</span>
            <span>Today</span>
          </div>
        </div>

        {/* Module Usage + Mood Distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Module Usage */}
          <div data-testid="module-usage" className="bg-card border border-border/50 rounded-2xl p-5">
            <p className="text-sm font-semibold mb-1">Most-Used Guides</p>
            <p className="text-[11px] text-muted-foreground mb-4">All-time message distribution</p>
            {data.module_usage.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">Start conversations to see your usage</p>
            ) : (
              <div className="space-y-2.5">
                {data.module_usage.slice(0, 6).map(m => {
                  const meta = MODULE_META[m.module] || { label: m.module, icon: MessageSquare, color: "text-zinc-500", bg: "bg-zinc-500" };
                  const pct = Math.round((m.messages / maxModuleUsage) * 100);
                  return (
                    <div key={m.module} data-testid={`module-${m.module}`} className="flex items-center gap-3">
                      <meta.icon className={cn("w-4 h-4 flex-shrink-0", meta.color)} strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium truncate">{meta.label}</p>
                          <p className="text-[11px] text-muted-foreground ml-2">{m.messages}</p>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted/30">
                          <div className={cn("h-full rounded-full transition-all", meta.bg)} style={{ width: `${pct}%`, opacity: 0.7 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Mood Distribution */}
          <div data-testid="mood-distribution" className="bg-card border border-border/50 rounded-2xl p-5">
            <p className="text-sm font-semibold mb-1">Mood Distribution</p>
            <p className="text-[11px] text-muted-foreground mb-4">90-day emotional balance</p>
            {totalMoodDist <= 1 && data.mood_distribution.positive === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">Log moods to see your distribution</p>
            ) : (
              <>
                <div className="flex h-3 rounded-full overflow-hidden mb-4">
                  <div className="bg-emerald-500 transition-all" style={{ width: `${(data.mood_distribution.positive / totalMoodDist) * 100}%` }} />
                  <div className="bg-amber-500 transition-all" style={{ width: `${(data.mood_distribution.neutral / totalMoodDist) * 100}%` }} />
                  <div className="bg-red-400 transition-all" style={{ width: `${(data.mood_distribution.negative / totalMoodDist) * 100}%` }} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <p className="text-lg font-bold text-emerald-500">{data.mood_distribution.positive}</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Positive</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <p className="text-lg font-bold text-amber-500">{data.mood_distribution.neutral}</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Neutral</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <p className="text-lg font-bold text-red-400">{data.mood_distribution.negative}</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Negative</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mood Trend (Weekly Averages) */}
        <div data-testid="mood-trend-chart" className="bg-card border border-border/50 rounded-2xl p-5">
          <p className="text-sm font-semibold mb-1">Mood Trend</p>
          <p className="text-[11px] text-muted-foreground mb-4">Weekly average scores — last 12 weeks</p>
          <div className="flex items-end gap-1 h-20">
            {data.mood_weekly.map((w, i) => {
              const score = w.avg_score;
              const color = score === null ? "bg-muted/20" : score >= 7 ? "bg-emerald-500" : score >= 4 ? "bg-amber-500" : "bg-red-400";
              const height = score !== null ? (score / 10) * 100 : 5;
              return (
                <div key={i} className="flex-1 flex flex-col items-center group relative">
                  <div className={cn("w-full max-w-[16px] rounded-t-sm transition-all", color)} style={{ height: `${height * 0.8}px`, opacity: score === null ? 0.2 : 0.8 }} />
                  {score !== null && (
                    <div className="absolute bottom-full mb-1 hidden group-hover:block bg-popover border border-border rounded-lg px-2 py-1 text-[10px] whitespace-nowrap shadow-lg z-10">
                      {w.week}: {score}/10 ({w.entries} logs)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-muted-foreground/50">
            <span>12w ago</span>
            <span>This week</span>
          </div>
        </div>

        {/* Top Emotions */}
        {data.top_emotions.length > 0 && (
          <div data-testid="top-emotions" className="bg-card border border-border/50 rounded-2xl p-5">
            <p className="text-sm font-semibold mb-3">Top Emotions</p>
            <div className="flex flex-wrap gap-2">
              {data.top_emotions.map((e, i) => (
                <span key={e.emotion} className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                  i === 0 ? "bg-primary/10 border-primary/30 text-primary" : "bg-muted/30 border-border/50 text-muted-foreground"
                )}>
                  {e.emotion} <span className="text-[10px] opacity-60">×{e.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
